import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Candidate, Dossier, Judgement, Phase, Ranker, RerankPass, SearchProvider, Timing } from "./types";
import { FixtureSearchProvider } from "./fixture-provider";
import { MockRanker } from "./mock-ranker";

export interface Progress {
  scored: number;
  total: number;
}

export interface DossierController {
  query: string;
  phase: Phase;
  dossier: Dossier | null;
  judgements: Record<string, Judgement>;
  backend: string;
  progress: Progress;
  timing: Timing;
  error: string | null;
  stale: boolean;
  submit: (query: string) => void;
  cancel: () => void;
}

function dedupe(items: Candidate[]): Candidate[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function buildPasses(dossier: Dossier): RerankPass[] {
  return [
    { pass: 1 as const, items: dedupe(dossier.pairs.flatMap((p) => [p.statute, p.case])) },
    { pass: 2 as const, items: dedupe(dossier.statutes) },
    { pass: 3 as const, items: dedupe(dossier.cases) },
  ].filter((p) => p.items.length > 0);
}

export function useDossier(
  provider: SearchProvider = useMemo(() => new FixtureSearchProvider(), []),
  ranker: Ranker = useMemo(() => new MockRanker(), []),
): DossierController {
  const [query, setQuery] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [judgements, setJudgements] = useState<Record<string, Judgement>>({});
  const [backend, setBackend] = useState(ranker.backend);
  const [progress, setProgress] = useState<Progress>({ scored: 0, total: 0 });
  const [timing, setTiming] = useState<Timing>({});
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const epochRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const dossierRef = useRef<Dossier | null>(null);

  useEffect(() => {
    let cancelled = false;
    void ranker.init().then(() => {
      if (!cancelled) setBackend(ranker.backend);
    });
    return () => {
      cancelled = true;
      // NOTE: 요청은 abort하지 않는다. StrictMode의 simulated unmount에서
      // 진행 중인 첫 요청이 조용히 죽고 phase가 'searching'에 갇히기 때문.
      // 실제 취소는 cancel()과 새 submit()이 담당한다.
    };
  }, [ranker]);

  const submit = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text) return;

      const epoch = ++epochRef.current;
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setQuery(text);
      setPhase("searching");
      setProgress({ scored: 0, total: 0 });
      setTiming({});
      setError(null);
      setStale(dossierRef.current !== null);

      void (async () => {
        const t0 = performance.now();
        try {
          const next = await provider.search(text, epoch, ac.signal);
          if (epoch !== epochRef.current || ac.signal.aborted) return;

          dossierRef.current = next;
          setDossier(next);
          setJudgements({});
          setStale(false);
          setPhase("hydrating");

          const passes = buildPasses(next);
          const total = new Set(passes.flatMap((p) => p.items.map((i) => i.id))).size;
          setProgress({ scored: 0, total });

          const scored = new Set<string>();
          for await (const batch of ranker.rerank({
            epoch,
            query: text,
            passes,
            signal: ac.signal,
          })) {
            if (epoch !== epochRef.current || ac.signal.aborted) return;

            const now = performance.now() - t0;
            for (const j of batch.judgements) scored.add(j.id);

            setJudgements((prev) => {
              const merged = { ...prev };
              for (const j of batch.judgements) merged[j.id] = j;
              return merged;
            });
            setProgress({ scored: scored.size, total });
            setTiming((prev) => ({
              firstBatch: prev.firstBatch ?? now,
              total: now,
            }));
          }

          if (epoch !== epochRef.current || ac.signal.aborted) return;
          setPhase("done");
          setTiming((prev) => ({ ...prev, total: performance.now() - t0 }));
        } catch (e) {
          if (epoch !== epochRef.current) return;
          if (ac.signal.aborted) {
            setStale(false);
            setPhase("cancelled");
            return;
          }
          setStale(false);
          setError(e instanceof Error ? e.message : String(e));
          setPhase("error");
        }
      })();
    },
    [provider, ranker],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return {
    query,
    phase,
    dossier,
    judgements,
    backend,
    progress,
    timing,
    error,
    stale,
    submit,
    cancel,
  };
}
